'use client';

import { useMemo, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useQueryState } from 'nuqs';
import { SimpleGrid } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import BrandSkeleton from './components/BrandSkeleton';
import mikaService from '@/_service/mika.service';
import { useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { findActivePoint } from '@/_utils/location';
import BranchCard from './components/branch-card';
import { useInfiniteScroll } from '@/app/[locale]/(home)/_components/Products/utils';
import LoadingSpinner from '@/app/[locale]/(home)/_components/Products/components/loading-spinner';

const LIMIT = 20;

const Brands = ({ initialPage }: { initialPage?: any }) => {
  const [search] = useQueryState('search');
  const [filterQuery] = useQueryState('filter');
  const [tagId] = useQueryState('tag_id');
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { points } = useSelector((state: IRedux) => state.common);

  const activePoint = useMemo(() => {
    return findActivePoint(points);
  }, [points]);

  const hasClientFilters = Boolean(search || filterQuery || tagId);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['branches-list', search, filterQuery, activePoint?.location, tagId],
    queryFn: ({ pageParam = 1 }) =>
      mikaService.getBranchList({
        search: search || '',
        shipper_tag_ids: tagId || undefined,
        lat: filterQuery === 'nearby' ? activePoint?.location?.[0] : undefined,
        long: filterQuery === 'nearby' ? activePoint?.location?.[1] : undefined,
        page: pageParam,
        limit: LIMIT,
        is_open: filterQuery === 'open',
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const count = parseInt(lastPage?.data?.count ?? '0', 10);
      const loaded = allPages.reduce(
        (sum, p) => sum + (p?.data?.branches?.length ?? 0),
        0,
      );
      return loaded < count ? allPages.length + 1 : undefined;
    },
    initialData:
      !hasClientFilters && initialPage
        ? {
            pages: [initialPage],
            pageParams: [1],
          }
        : undefined,
  });

  useInfiniteScroll(
    loadMoreRef,
    !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  );

  const branches = data?.pages.flatMap((page) => page?.data?.branches ?? []) ?? [];

  if (isLoading && !branches.length)
    return (
      <Box
        px={{ base: 4, lg: 14 }}
        pt={{ base: 3, lg: 5 }}
        pb={32}
      >
        <BrandSkeleton />
      </Box>
    );

  return (
    <Box
      id="branches-list"
      px={{ base: 4, lg: 14 }}
      pt={{ base: 3, lg: 5 }}
      pb={32}
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="5">
        {branches.map((item) => (
          <BranchCard key={item.id} branch={item} />
        ))}
      </SimpleGrid>
      {hasNextPage && (
        <Flex justifyContent="center" py={4} ref={loadMoreRef}>
          {isFetchingNextPage && <LoadingSpinner />}
        </Flex>
      )}
    </Box>
  );
};

export default Brands;
